/**
 * Typed wrappers for service-service-desk (port 3140 by default).
 *
 * Wired endpoints:
 *   - POST /v1/service-desk/cases-mine — citizen complaint submission.
 *   - GET  /v1/service-desk/cases-mine — citizen complaint list (read-own).
 *   - POST /v1/service-desk/cases      — operator-scoped complaint opening (staff).
 *
 * RBAC:
 *   - `cases-mine` requires `Complaint/create-own` / `Complaint/read-own`
 *     seeded to the `customer` role via 20260508_customer_complaint_perms.sql.
 *   - `cases` requires `ServiceDesk/openCase` (operator-only).
 *
 * Mirrors `apps/service-service-desk/src/modules/case/case.dto.ts`.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export type CasePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface OpenCaseRequest {
  readonly formKey: string;
  readonly customerId?: string;
  readonly title: string;
  readonly formData: Record<string, unknown>;
  readonly subjectRef?: string;
  readonly priority?: CasePriority;
}

export interface CaseResponse {
  readonly id: string;
  readonly caseNumber: string;
  readonly status: string;
  readonly priority: CasePriority;
  readonly title: string;
  readonly formKey: string;
  readonly category: string;
  readonly createdAt: string;
}

export async function openCase(
  body: OpenCaseRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & { bearerToken?: string } = {},
): Promise<CaseResponse> {
  const { bearerToken, ...rest } = options;
  return requestJson<CaseResponse>({
    method: 'POST',
    baseUrl: env.serviceServiceDeskBaseUrl,
    path: '/v1/service-desk/cases',
    body,
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}

/**
 * Citizen self-service: file a complaint via the citizen-scoped endpoint.
 * Requires `Complaint/create-own` permission (customer role).
 */
export async function openCitizenCase(
  body: OpenCaseRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & { bearerToken?: string } = {},
): Promise<CaseResponse> {
  const { bearerToken, ...rest } = options;
  return requestJson<CaseResponse>({
    method: 'POST',
    baseUrl: env.serviceServiceDeskBaseUrl,
    path: '/v1/service-desk/cases-mine',
    body,
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}

export interface CaseListResponse {
  readonly items: CaseResponse[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

/**
 * Citizen self-service: list own complaints.
 * Requires `Complaint/read-own` permission (customer role).
 */
export async function listCitizenCases(
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & { bearerToken?: string } = {},
): Promise<CaseListResponse> {
  const { bearerToken, ...rest } = options;
  return requestJson<CaseListResponse>({
    method: 'GET',
    baseUrl: env.serviceServiceDeskBaseUrl,
    path: '/v1/service-desk/cases-mine',
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}
